import { Grid } from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { FormProvider } from "react-hook-form";

import type { useDocumentDetailsForm } from "../hooks/useDocumentDetailsForm";
import type { useDocumentMetadata } from "../hooks/useDocumentMetadata";
import type { TabSectionHandle } from "../types";

import { DocumentMetadataFormSections } from "./DocumentMetadataFormSections";

type DocumentDetailsSectionProps = {
  form: ReturnType<typeof useDocumentDetailsForm>;
  document: ReturnType<typeof useDocumentMetadata>["document"];
  suggestedFacilities: ReturnType<typeof useDocumentMetadata>["suggestedFacilities"];
  suggestedFacilitiesLoading: boolean;
  tenantId: string;
};

export const DocumentDetailsSection = forwardRef<TabSectionHandle, DocumentDetailsSectionProps>(
  function DocumentDetailsSection(
    { form, document, suggestedFacilities, suggestedFacilitiesLoading, tenantId },
    ref
  ) {
    const save = useCallback(async () => {
      const isValid = await form.methods.trigger();
      if (!isValid) {
        throw new Error("Form validation failed");
      }
      return form.save(form.methods.getValues());
    }, [form]);

    useImperativeHandle(ref, () => ({ save, isDirty: form.isDirty }), [save, form.isDirty]);

    return (
      <FormProvider {...form.methods}>
        <Grid container rowSpacing={1.5} columnSpacing={3}>
          <DocumentMetadataFormSections
            control={form.control}
            document={document}
            suggestedFacilities={suggestedFacilities}
            suggestedFacilitiesLoading={suggestedFacilitiesLoading}
            documentTypeOptions={form.documentTypeOptions}
            tenantId={tenantId}
          />
        </Grid>
      </FormProvider>
    );
  }
);
